import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import ReactTooltip from 'react-tooltip';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import Utils from 'common/services/Utils';
import { ContractExpiringDashboardDto } from 'api/dashboard/dtos/ContractExpiringDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import dayjs from 'dayjs';
import { DATE_FORMAT_DEFAUT } from 'Config';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import KpiHeader from '../components/KpiHeader';
import useWidgetFilters from '../hooks/useWidgetFilters';
import { useDashboardVehicleScope, vehicleScopeCriteria } from '../hooks/useDashboardVehicleScope';
import useInfiniteReveal from '../components/useInfiniteReveal';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import styles from './ContractsExpiring.module.scss';
import infiniteStyles from '../components/useInfiniteReveal.module.scss';

const PAGE_SIZE = 15;

type PeriodOption = '30' | '60' | '90' | 'custom';

type Props = {
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

const getDaysBadgeClass = (days: number) => {
	if (days <= 30) return styles.badgeRed;
	if (days <= 60) return styles.badgeAmber;
	return styles.badgeBlue;
};

const ContractsExpiring = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<ContractExpiringDashboardDto[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [period, setPeriod] = useState<PeriodOption>('90');
	const { dateFrom: customFrom, dateTo: customTo, setDateRange } = useWidgetFilters('contractsExpiring');
	const vehicleScope = vehicleScopeCriteria(useDashboardVehicleScope());

	const getDateRange = () => {
		if (period === 'custom') {
			return { dateFrom: customFrom, dateTo: customTo };
		}
		const days = parseInt(period, 10);
		return {
			dateFrom: dayjs().startOf('day').toDate(),
			dateTo: dayjs().add(days, 'day').endOf('day').toDate(),
		};
	};

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const range = getDateRange();
				const criteria = {
					...(props.filters || {}),
					...vehicleScope,
					dateFrom: range.dateFrom,
					dateTo: range.dateTo,
				};
				const result = await DashboardService.getContractsExpiring(criteria);
				if (!cancelled) {
					setData(result || []);
				}
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, period, customFrom, customTo]);

	const { visibleItems, sentinelRef, hasMore } = useInfiniteReveal(data, PAGE_SIZE);

	const navigateToContractDetail = (item: ContractExpiringDashboardDto) => {
		const type = item.contractType.toLowerCase();
		window.open(`/vehicles/details/${item.vehicleId}/contract/details/${type}/details/${item.contractId}`, '_blank');
	};

	const buildVehicleTooltip = (item: ContractExpiringDashboardDto) =>
		[item.brand, item.model].filter(Boolean).join(' ') || undefined;

	const handlePeriodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setPeriod(e.target.value as PeriodOption);
	};

	const totalMonthlyCost = data.reduce((sum, item) => sum + (item.monthlyCost || 0), 0);

	const renderOptions = () => (
		<div className={styles.optionsRow}>
			<select
				className={styles.periodSelect}
				value={period}
				onChange={handlePeriodChange}
			>
				<option value="30">{t('dashboard.contract_period_30' as any)}</option>
				<option value="60">{t('dashboard.contract_period_60' as any)}</option>
				<option value="90">{t('dashboard.contract_period_90' as any)}</option>
				<option value="custom">{t('dashboard.contract_period_custom' as any)}</option>
			</select>
			{period === 'custom' && (
				<WidgetMonthPicker dateFrom={customFrom} dateTo={customTo} onChange={setDateRange} />
			)}
		</div>
	);

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.contracts_expiring') as string}
			renderOptions={renderOptions()}
			subTitleTooltip={t('dashboard.contracts_expiring_tooltip' as any) as string}
		>
			{isLoading ? (
				<div className={tableStyles.centerAlign}>
					<LoadingSpinner />
				</div>
			) : data.length === 0 ? (
				<WidgetNoData />
			) : (
				<>
					<KpiHeader
						value={Utils.formatCurrency(totalMonthlyCost)}
						subLabel={`${data.length} · ${t('dashboard.contracts_expiring_subtitle' as any)}`}
						currentTotal={totalMonthlyCost}
						previousTotal={null}
						previousRange={null}
					/>
					<div className={styles.tableWrapper}>
						<table className={styles.table}>
							<thead>
								<tr>
									<th>{t('dashboard.columns.vehicle')}</th>
									<th>{t('dashboard.contract_columns.type' as any)}</th>
									<th>{t('dashboard.contract_columns.number' as any)}</th>
									<th>{t('dashboard.contract_columns.end_date' as any)}</th>
									<th>{t('dashboard.contract_columns.term' as any)}</th>
									<th className={styles.textRight}>{t('dashboard.contract_columns.monthly_cost' as any)}</th>
								</tr>
							</thead>
							<tbody>
								{visibleItems.map((item, index) => (
									<tr
										key={`${item.contractId}-${index}`}
										className={styles.tableRow}
										onClick={() => navigateToContractDetail(item)}
									>
										<td><span className={styles.plate}><span data-tip={buildVehicleTooltip(item)} data-for="contracts-expiring-vehicle-tooltip" data-place="right">{item.registrationNumber}</span></span></td>
										<td>{t(`dashboard.contract_type_${item.contractType.toLowerCase()}` as any)}</td>
										<td>{item.contractNumber || <span className={styles.muted}>—</span>}</td>
										<td>{dayjs(item.endDate).format(DATE_FORMAT_DEFAUT)}</td>
										<td>
											<span className={`${styles.badge} ${getDaysBadgeClass(item.daysToExpiration)}`}>
												{t('dashboard.contract_days_left' as any, { count: item.daysToExpiration })}
											</span>
										</td>
										<td className={styles.textRight}>{Utils.formatCurrency(item.monthlyCost)}</td>
									</tr>
								))}
								{hasMore && (
									<tr ref={sentinelRef as unknown as React.Ref<HTMLTableRowElement>}>
										<td colSpan={6} className={infiniteStyles.sentinelRow} />
									</tr>
								)}
							</tbody>
						</table>
					</div>
					<ReactTooltip id="contracts-expiring-vehicle-tooltip" className={styles.tooltip} effect="solid" multiline={true} />
				</>
			)}
		</Box>
	);
};

export default ContractsExpiring;
